import { FormEvent, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle2, History, Link2, Loader2, Play, Search, Unlink, Youtube as YoutubeIcon } from 'lucide-react'
import { youtube } from '../api/client'

const STARTER_SEARCHES = ['live concerts', 'documentaries', 'technology', 'music videos']

export default function Youtube() {
  const [params, setParams] = useSearchParams()
  const searchedQuery = params.get('q') || ''
  const [query, setQuery] = useState(searchedQuery)
  const [showConnect, setShowConnect] = useState(false)
  const [cookies, setCookies] = useState('')
  const [feedKind, setFeedKind] = useState<'home' | 'subscriptions'>('home')
  const queryClient = useQueryClient()

  const { data: connection } = useQuery({
    queryKey: ['youtube', 'connection'],
    queryFn: youtube.connection,
  })
  const { data: feed, isLoading: feedLoading, error: feedError } = useQuery({
    queryKey: ['youtube', 'feed', feedKind],
    queryFn: () => youtube.feed(24, feedKind),
    enabled: connection?.connected === true && !searchedQuery,
  })
  const { data: history } = useQuery({
    queryKey: ['youtube', 'history'],
    queryFn: youtube.history,
    enabled: connection?.connected === true && !searchedQuery,
  })
  const connect = useMutation({
    mutationFn: () => youtube.connect(cookies),
    onSuccess: () => {
      setCookies('')
      setShowConnect(false)
      queryClient.invalidateQueries({ queryKey: ['youtube'] })
    },
  })
  const disconnect = useMutation({
    mutationFn: youtube.disconnect,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['youtube'] }),
  })

  useEffect(() => setQuery(searchedQuery), [searchedQuery])

  const { data: results, isLoading, error } = useQuery({
    queryKey: ['youtube', 'search', searchedQuery],
    queryFn: () => youtube.search(searchedQuery),
    enabled: searchedQuery.length >= 2,
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const value = query.trim()
    if (value.length >= 2) setParams({ q: value })
  }

  return (
    <div className="min-h-full overflow-x-hidden bg-dark-900 text-dark-100">
      <section className="relative overflow-hidden border-b border-dark-800 bg-dark-950/40 px-4 py-8 sm:px-8 lg:px-12 lg:py-12">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(34,197,94,0.16),transparent_34%),radial-gradient(circle_at_90%_0%,rgba(59,130,246,0.10),transparent_30%)]" />
        <div className="relative mx-auto w-full max-w-7xl">
          <div className="mb-6 flex items-center gap-3 text-primary-400">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-dark-950 shadow-lg shadow-primary-950/30">
              <YoutubeIcon size={23}  />
            </span>
            <span className="text-sm font-semibold uppercase tracking-[0.28em]">Internet video</span>
          </div>
          <h1 className="max-w-3xl text-4xl font-black tracking-tight text-white sm:text-5xl lg:text-6xl">
            YouTube in S3ND Media
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-dark-400 sm:text-lg">
            Search videos, play in the bottom bar, or cast to a registered Pi.
          </p>

          <form onSubmit={submit} className="mt-8 flex w-full max-w-3xl flex-col gap-3 sm:flex-row">
            <label className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-500" size={21} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search YouTube"
                autoFocus
                className="h-14 w-full rounded-2xl border border-dark-700 bg-dark-900 pl-12 pr-4 text-lg outline-none transition focus:border-primary-400/70 focus:bg-dark-850"
              />
            </label>
            <button
              type="submit"
              disabled={query.trim().length < 2}
              className="h-14 rounded-2xl bg-primary-500 px-7 font-bold text-dark-950 transition hover:bg-primary-400 disabled:cursor-not-allowed disabled:bg-dark-700 disabled:text-dark-500"
            >
              Search
            </button>
          </form>

          {!searchedQuery && (
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {STARTER_SEARCHES.map((item) => (
                <button
                  key={item}
                  onClick={() => setParams({ q: item })}
                  className="rounded-full border border-dark-700 bg-dark-800/70 px-4 py-2 text-sm text-dark-300 transition hover:border-primary-400/50 hover:text-white"
                >
                  {item}
                </button>
              ))}
              <button
                onClick={() => connection?.connected ? disconnect.mutate() : setShowConnect(true)}
                className="ml-auto inline-flex items-center gap-2 rounded-full border border-primary-400/30 bg-primary-500/10 px-4 py-2 text-sm font-bold text-primary-200"
              >
                {connection?.connected ? <><CheckCircle2 size={16} /> {connection.account_label ? `Connected as ${connection.account_label}` : 'YouTube connected'}</> : <><Link2 size={16} /> Connect YouTube</>}
              </button>
            </div>
          )}

          {showConnect && (
            <div className="mt-6 max-w-3xl rounded-2xl border border-primary-400/25 bg-dark-950/80 p-5 backdrop-blur">
              <h2 className="font-bold">Connect your YouTube account</h2>
              <p className="mt-2 text-sm leading-6 text-dark-400">
                Export youtube.com cookies in Netscape format from a browser where you are already signed in, then paste the file contents here. Cookies are encrypted before storage and never sent to a Pi.
              </p>
              <textarea value={cookies} onChange={(event) => setCookies(event.target.value)} rows={5} placeholder="# Netscape HTTP Cookie File" className="mt-4 w-full rounded-xl border border-dark-700 bg-dark-950 p-3 font-mono text-xs outline-none focus:border-primary-400/60" />
              {connect.error && <p className="mt-2 text-sm text-danger-300">{connect.error instanceof Error ? connect.error.message : 'Connection failed'}</p>}
              <div className="mt-3 flex gap-3">
                <button onClick={() => connect.mutate()} disabled={!cookies.trim() || connect.isPending} className="rounded-xl bg-primary-500 px-5 py-2.5 text-dark-950 font-bold disabled:opacity-40">{connect.isPending ? 'Connecting...' : 'Save connection'}</button>
                <button onClick={() => setShowConnect(false)} className="rounded-xl bg-dark-800 px-5 py-2.5">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-8 lg:px-12">
        {isLoading && (
          <div className="flex min-h-64 items-center justify-center gap-3 text-dark-400">
            <Loader2 className="animate-spin text-primary-400" /> Resolving YouTube results...
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-danger-500/30 bg-danger-500/10 p-5 text-danger-200">
            YouTube search failed. The server may need a newer yt-dlp build. {error instanceof Error ? error.message : ''}
          </div>
        )}

        {results && (
          <>
            <div className="mb-5 flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-primary-400">Search results</p>
                <h2 className="mt-1 text-2xl font-bold">{searchedQuery}</h2>
              </div>
              <span className="text-sm text-dark-500">{results.length} videos</span>
            </div>
            <div className="grid min-w-0 gap-x-5 gap-y-8 sm:grid-cols-2 xl:grid-cols-3">
              {results.map((video, index) => (
                <Link
                  key={video.id}
                  to={`/youtube/watch/${video.id}`}
                  className="group animate-[youtube-rise_.45s_ease-out_both]"
                  style={{ animationDelay: `${Math.min(index * 45, 360)}ms` }}
                >
                  <div className="relative aspect-video overflow-hidden rounded-2xl bg-dark-800 shadow-xl shadow-black/20 ring-1 ring-dark-700">
                    {video.thumbnail ? (
                      <img src={video.thumbnail} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]" loading="lazy" />
                    ) : (
                      <div className="flex h-full items-center justify-center bg-[linear-gradient(135deg,#27120e,#121518)]">
                        <YoutubeIcon size={48} className="text-primary-500" />
                      </div>
                    )}
                    <span className="absolute bottom-3 right-3 rounded-md bg-black/85 px-2 py-1 text-xs font-bold">{video.duration}</span>
                    <span className="absolute inset-0 flex items-center justify-center bg-black/0 transition group-hover:bg-black/30">
                      <span className="flex h-14 w-14 scale-75 items-center justify-center rounded-full bg-primary-500 opacity-0 shadow-xl transition group-hover:scale-100 group-hover:opacity-100">
                        <Play fill="white" size={23} className="ml-1" />
                      </span>
                    </span>
                  </div>
                  <h3 className="mt-3 line-clamp-2 text-base font-bold leading-snug transition group-hover:text-primary-400">{video.title}</h3>
                  <p className="mt-1 text-sm text-dark-500">{video.channel}</p>
                </Link>
              ))}
            </div>
          </>
        )}

        {!searchedQuery && connection?.connected && (
          <>
            <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
              <div><p className="text-xs font-bold uppercase tracking-[0.24em] text-primary-400">Your YouTube</p><h2 className="mt-1 text-2xl font-bold">{feedKind === 'home' ? 'Recommended for you' : 'Latest from subscriptions'}</h2>{connection.account_label && <p className="mt-1 text-sm text-dark-500">Signed in as {connection.account_label}</p>}</div>
              <div className="flex items-center gap-2">
                <button onClick={() => setFeedKind('home')} className={`rounded-full px-4 py-2 text-sm font-bold ${feedKind === 'home' ? 'bg-primary-500 text-dark-950' : 'bg-white/5 text-dark-400'}`}>Home</button>
                <button onClick={() => setFeedKind('subscriptions')} className={`rounded-full px-4 py-2 text-sm font-bold ${feedKind === 'subscriptions' ? 'bg-primary-500 text-dark-950' : 'bg-white/5 text-dark-400'}`}>Subscriptions</button>
                <button onClick={() => disconnect.mutate()} className="ml-2 inline-flex items-center gap-2 text-sm text-dark-500 hover:text-white"><Unlink size={15} /> Disconnect</button>
              </div>
            </div>
            {feedLoading && <div className="flex min-h-48 items-center justify-center gap-3 text-dark-400"><Loader2 className="animate-spin text-primary-400" /> Loading your feed...</div>}
            {feedError && <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-5 text-amber-100">Your YouTube session needs attention. Reconnect with a fresh cookie export.</div>}
            {feed && <div className="grid min-w-0 gap-x-5 gap-y-8 sm:grid-cols-2 xl:grid-cols-3">{feed.map((video) => (
              <Link key={video.id} to={`/youtube/watch/${video.id}`} className={video.watched ? 'group opacity-60' : 'group'}>
                <div className="relative aspect-video overflow-hidden rounded-2xl bg-dark-800 ring-1 ring-dark-700">
                  <img src={video.thumbnail || undefined} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]" />
                  <span className="absolute bottom-3 right-3 rounded-md bg-black/85 px-2 py-1 text-xs font-bold">{video.duration}</span>
                  {video.watched && <span className="absolute left-3 top-3 rounded-full bg-black/80 px-3 py-1 text-xs font-bold">Watched</span>}
                  {video.position_seconds > 0 && !video.watched && <span className="absolute inset-x-0 bottom-0 h-1 bg-primary-500" />}
                </div>
                <h3 className="mt-3 line-clamp-2 font-bold group-hover:text-primary-400">{video.title}</h3><p className="mt-1 text-sm text-dark-500">{video.channel}</p>
              </Link>
            ))}</div>}
            {history && history.length > 0 && (
              <div className="mt-12 border-t border-dark-800 pt-8">
                <div className="mb-5 flex items-center gap-3"><History className="text-primary-400" size={22} /><h2 className="text-2xl font-bold">Continue watching</h2></div>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">{history.filter((item) => !item.watched && item.position_seconds > 0 && item.title).slice(0, 8).map((item) => (
                  <Link key={item.video_id} to={`/youtube/watch/${item.video_id}`} className="group">
                    <div className="relative aspect-video overflow-hidden rounded-xl bg-dark-800 ring-1 ring-dark-700"><img src={item.thumbnail || undefined} alt="" className="h-full w-full object-cover" /><span className="absolute inset-x-0 bottom-0 h-1 bg-primary-500" /></div>
                    <h3 className="mt-2 line-clamp-2 text-sm font-bold group-hover:text-primary-400">{item.title}</h3><p className="text-xs text-dark-500">{item.channel}</p>
                  </Link>
                ))}</div>
              </div>
            )}
          </>
        )}

        {!searchedQuery && !connection?.connected && !isLoading && (
          <div className="grid min-h-64 place-items-center rounded-3xl border border-dashed border-dark-700 bg-dark-950/40 text-center">
            <div>
              <YoutubeIcon className="mx-auto text-dark-700" size={48} />
              <p className="mt-4 font-semibold text-dark-300">Start with a search</p>
              <p className="mt-1 text-sm text-dark-600">Public videos are resolved by the S3ND Media server.</p>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
